import { EditOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, Select, Switch, Tooltip } from "antd";
import { useForm } from "antd/es/form/Form";
import { Modal } from "antd/lib";
import React, { useEffect, useMemo, useState } from "react";
import { apiRequests } from "src/shared/api/api";

const EditUser = ({ users, record, onUserUpdated }) => {
  const [visible, setVisible] = useState(false);
  const [form] = useForm();

  const usersOptions = useMemo(() => {
    const uniqueIds = new Set();

    return users
      .filter((user) => {
        // Проверяем уникальность пользователя
        if (uniqueIds.has(user.id)) return false;
        uniqueIds.add(user.id);
        return true;
      })
      .filter((user) => user.id !== record.id)
      .map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.id,
      }));
  }, [users, record.id]);

  const getTimeZoneOffsetMinutes = (timeZone, date = new Date()) => {
    const dtf = new Intl.DateTimeFormat("en-US", {
      hour12: false,
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = dtf.formatToParts(date).reduce((acc, part) => {
      if (part.type !== "literal") acc[part.type] = part.value;
      return acc;
    }, {});
    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    // разница в минутах: (время с теми же компонентами как UTC) - (реальное UTC время)
    return Math.round((asUTC - date.getTime()) / 60000);
  };

  const formatGMTOffset = (minutes) => {
    const sign = minutes >= 0 ? "+" : "-";
    const abs = Math.abs(minutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, "0");
    const mm = String(abs % 60).padStart(2, "0");
    return `GMT${sign}${hh}:${mm}`;
  };

  const timezonesOptions = useMemo(() => {
    const now = new Date();
    return Intl.supportedValuesOf("timeZone").map((zone) => {
      const offsetMin = getTimeZoneOffsetMinutes(zone, now);
      return {
        value: zone,
        label: `${zone} ${formatGMTOffset(offsetMin)}`,
      };
    });
  }, []);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(record);
    }
  }, [visible, record, form]);

  const onFinish = () => {
    const body = form.getFieldsValue();
    const shiftWorkEnabled = body.shift_work_enabled;
    delete body.shift_work_enabled;
    delete body.users;

    const selectedUserIds = form.getFieldValue("users") || [];

    apiRequests.users.put(body, record.id).then(() => {
      onUserUpdated?.();

      const updatePromises = selectedUserIds
        .filter((id) => id !== record.id)
        .map((id) => apiRequests.users.put(body, id));

      Promise.all(updatePromises).then(() => {
        onUserUpdated?.();
      });
    });

    if (shiftWorkEnabled !== undefined) {
      apiRequests.users.patchShiftSettings(record.id, {
        shift_work_enabled: shiftWorkEnabled,
      });
    }

    setVisible(false);
    form.resetFields();
  };

  return (
    <>
      <Tooltip title="Редактировать пользователя">
        <Button icon={<EditOutlined />} onClick={() => setVisible(true)} />
      </Tooltip>

      <Modal
        open={visible}
        onCancel={() => setVisible(false)}
        title="Редактирование пользователя"
        footer={null}
      >
        <Form form={form} onFinish={onFinish}>
          <Form.Item name="tags" label="Теги">
            <Select mode="tags" />
          </Form.Item>
          <Form.Item label="Применить также к" name="users">
            <Select
              options={usersOptions}
              mode="multiple"
              optionFilterProp="label"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="payment_past_edit_days"
            label="Количество дней для изменения платежей"
          >
            <InputNumber
              step={1}
              precision={0}
              min={0}
              style={{ width: "100%" }}
              placeholder={"Неограниченно"}
            />
          </Form.Item>
          <Form.Item name="timezone" label="Временная зона">
            <Select
              options={timezonesOptions}
              style={{ width: "100%" }}
              maxCount={1}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="shift_work_enabled"
            label="Сменный график"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" type="primary">
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EditUser;
